import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../lib/apiClient.js';
import { SESSION_CHANGED_EVENT, subscribeToSessionChanges } from './sessionEvents.js';

const SiteAccessContext = createContext(null);

export function isSiteAccessBlocked(state) {
  if (!state) {
    return false;
  }
  return (
    state.gateMode === 'enforced' &&
    !state.automationBypass &&
    !state.isOwner &&
    state.status !== 'approved'
  );
}

export function SiteAccessProvider({ children }) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    state: null,
  });

  const refresh = useCallback(async () => {
    setState((current) => ({ ...current, loading: true }));
    try {
      const response = await api.get('/site-access/me');
      setState({
        loading: false,
        error: null,
        state: response?.data || null,
      });
    } catch (error) {
      // Fail open here so older deployments or transient API issues do not
      // brick the whole client. The server still remains the enforcement point.
      setState({
        loading: false,
        error,
        state: null,
      });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    async function handleSessionChanged(event) {
      if (event?.detail?.source === 'session-events') {
        return;
      }
      try {
        await refresh();
      } finally {
        event?.detail?.ack?.();
      }
    }

    window.addEventListener(SESSION_CHANGED_EVENT, handleSessionChanged);
    return () => {
      window.removeEventListener(SESSION_CHANGED_EVENT, handleSessionChanged);
    };
  }, [refresh]);

  useEffect(() => subscribeToSessionChanges(refresh), [refresh]);

  return (
    <SiteAccessContext.Provider
      value={{
        loading: state.loading,
        error: state.error,
        state: state.state,
        refresh,
      }}
    >
      {children}
    </SiteAccessContext.Provider>
  );
}

export function useSiteAccess() {
  const context = useOptionalSiteAccess();
  if (!context) {
    throw new Error('useSiteAccess must be used inside SiteAccessProvider');
  }
  return context;
}

export function useOptionalSiteAccess() {
  return useContext(SiteAccessContext);
}
